/**
 * Daily Quote — glassmorphic card with blur orbs.
 * Dismiss with X (slides up fast), reappears next day.
 * Like at bottom-left, Share at bottom-right.
 * Share via Telegram as plain text with @TurniflyBot mention.
 */

import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Heart, X, Send } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useTodayQuote, useToggleQuoteLike } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'
import type { TodayQuote } from '@turnifly/shared/types'
import { triggerHapticImpact } from '@turnifly/shared/utils'

const DISMISS_KEY = 'turnifly_quote_dismissed'

function isDismissedToday(): boolean {
  const stored = localStorage.getItem(DISMISS_KEY)
  if (!stored) return false
  return stored === new Date().toISOString().slice(0, 10)
}

function dismissForToday(): void {
  localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10))
}

export const DailyQuote: React.FC = () => {
  const { user } = useAuthStore()
  const userId = user?.id
  const { data, isLoading } = useTodayQuote(userId)
  const quote = data as TodayQuote | undefined
  const toggleLike = useToggleQuoteLike()
  const { i18n, t } = useTranslation()
  const [isDismissed, setIsDismissed] = useState(isDismissedToday)

  const handleDismiss = useCallback((): void => {
    triggerHapticImpact('light')
    dismissForToday()
    setIsDismissed(true)
  }, [])

  const handleLike = useCallback((): void => {
    if (!userId || !quote) return
    triggerHapticImpact('light')
    toggleLike.mutate({
      userId,
      quoteId: quote.id.toString(),
      isLiked: !!quote.is_liked,
    })
  }, [userId, quote, toggleLike])

  const handleShare = useCallback((): void => {
    if (!quote) return
    const text = i18n.language === 'en' && quote.text_en ? quote.text_en : quote.text_uz

    const shareText = [
      t('home.dailyQuoteTitle'),
      '',
      `"${text}"`,
      '',
      `— ${quote.author} —`,
      '',
      'https://t.me/TurniflyBot',
    ].join('\n')

    const encoded = encodeURIComponent(shareText)
    // In a Capacitor app `tg://share` doesn't open Telegram reliably,
    // so we use the universal HTTPS share link which works in browser,
    // PWA, and the native Telegram app.
    window.open(`https://t.me/share/url?url=&text=${encoded}`, '_blank')
  }, [quote, i18n.language, t])

  if (isLoading) {
    return (
      <div className="rounded-2xl p-4 bg-[#182533]">
        <Skeleton className="h-4 w-3/4 mb-2" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    )
  }

  if (!quote || isDismissed) return null

  const text = i18n.language === 'en' && quote.text_en ? quote.text_en : quote.text_uz

  return (
    <div className="quote-card rounded-2xl">
      {/* X dismiss — top right */}
        <button
          onClick={handleDismiss}
          className="absolute top-1.5 right-1.5 z-10 w-7 h-7 flex items-center justify-center rounded-full text-[#768c9e] hover:text-text hover:bg-white/[0.08] transition-colors tap-highlight-transparent"
        >
          <X size={14} />
        </button>

        {/* Quote content — centered */}
        <div className="relative z-[1] px-6 pt-4 pb-3 text-center">
          <p className="font-display text-[15px] leading-relaxed text-text/90 italic">
            &ldquo;{text}&rdquo;
          </p>
          <p className="text-[12px] font-semibold text-text-hint mt-2">
            — {quote.author} —
          </p>
        </div>

        {/* Bottom bar: like + share */}
        <div className="relative z-[1] flex items-center justify-between px-4 py-2.5 border-t border-white/[0.06]">
          <button
            onClick={handleLike}
            className="flex items-center gap-1.5 tap-highlight-transparent"
          >
            <motion.div
              animate={quote.is_liked ? { scale: [1, 1.25, 1] } : {}}
              transition={{ duration: 0.2 }}
            >
              <Heart
                size={16}
                className={`transition-colors duration-150 ${
                  quote.is_liked ? 'text-danger fill-danger' : 'text-text-hint/40'
                }`}
              />
            </motion.div>
            {(quote.likes_count ?? 0) > 0 && (
              <span className="text-[11px] font-medium text-[#768c9e]">
                {quote.likes_count}
              </span>
            )}
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 text-[#768c9e] hover:text-text transition-colors tap-highlight-transparent"
          >
            <Send size={14} />
            <span className="text-[11px] font-medium">{t('common.share')}</span>
          </button>
        </div>
      </div>
  )
}
